// One patient's promotion packages — `GET /api/promotion-patient?patient_id=`.
//
// The endpoint returns a BARE ARRAY (no paging envelope, [] when empty, already
// filtered active == true). Each row is the promotion_patient record plus:
//   max        (eval of promotion.max)              — the denominator
//   count      (addon treatment_promotionCount)     — the numerator
//   pattern[]  (eval of promotion.pattern)          — the schedule
//   treatment[]                                     — visits it was billed into
//   _promotion {id, Maintype, Subtype, Name, Detail}
//   _seller {code}
import { storedMax, readPattern, patternRows, baht } from './promotion.js'

export const asPackages = (r) => (Array.isArray(r) ? r : (r?.items || []))

/* ── n and N ───────────────────────────────────────────────────────────────── */

// n — uses consumed. `count` is the addon `treatment_promotionCount`, which is a
// `db.query treatment JOIN billing … return {type:"count"}` filtered on
// `billing.active`. That is byte-for-byte the number `promotion_patient/add` uses
// to choose the next step (`repeat_acc > ($billing|count)`), so it is the ONLY
// valid numerator.
export const packageUsed = (row) => Math.max(0, Number(row?.count) || 0)

// N — total uses the package can ever produce.
//
// ⚠️ `storedMax`, NEVER `patternMax`. patternMax() runs normalizePattern(), which
// re-derives repeat_acc from the `repeat` column — and `repeat` has DRIFTED on 23
// of 224 promotions (Invisalign Lite #105 stores repeat 1,1,1,2,1 against
// repeat_acc 2,4,6,14,15). Re-deriving would cut that package from 15 installments
// to 6. storedMax reads the highest stored repeat_acc, which is also the pair
// maxMismatch() checks and what the receipt denominator is printed from.
// `storedMax` FIRST, `max` only as a fallback. Both surfaces on this card have to
// come from ONE number: the header's denominator and the expanded step list (whose
// per-step counts are the GAPS in repeat_acc) must agree. Audited over all 224
// promotions 2026-08-12 — three disagree, and on all three `storedMax` is the one
// that matches what `POST /promotion_patient/add` actually walks and what the
// patient's own billing lines say:
//     #201 แพคเสริม สบายใจ      max 10 · storedMax 12 · receipts 12
//     #129 รากเทียม 29,000       max  1 · storedMax  2 · receipts  2
// (`lib/promotion.js`'s "all 224 agree" comment is out of date — see maxMismatch.)
// A package whose pattern EXISTS but accumulates to 0 can never bill anything.
// `promotion_patient/add` picks the first step with `repeat_acc > (billings so far)`,
// and the count starts at 0 — so a pattern topping out at 0 matches nothing, forever.
// Promotion #191 "Home Retainer Duo 5,239" is exactly this shape (`active: true`,
// one step at repeat_acc 0) and it is live in the catalog.
export const isPackageDead = (row) => patternRows(row).length > 0 && !storedMax(row)

// ⚠️ The `|| Number(row?.max)` fallback must NOT rescue a dead package. #191 stores
// max = 1, so falling through printed "0 / 1" — the front desk reads that as "one
// retainer set still owed", promises it, and nothing bills at the chair.
export const packageN = (row) =>
  (isPackageDead(row) ? 0 : (storedMax(row) || Number(row?.max) || 0))

// True when the catalog's stored `max` disagrees with its own pattern. Rare (3 of
// 224) but it means the receipts and the catalog say different things, which is
// Ram's to fix — so it is surfaced, not silently resolved.
export const packageMaxMismatch = (row) => {
  const stored = Number(row?.max)
  const computed = storedMax(row)
  if (!Number.isFinite(stored)) return null
  // No pattern at all = nothing to compare against (api 185 can strip `pattern`).
  // But a pattern that sums to 0 is NOT "no data" — it is #191, the one row in the
  // catalog that can never bill. The old `|| !computed` guard returned null for
  // exactly that case, so the single most broken package looked healthy.
  if (!patternRows(row).length) return null
  return stored === computed ? null : { stored, computed }
}

/* ── state ─────────────────────────────────────────────────────────────────── */
// 'unused' nothing redeemed · 'active' partway · 'done' fully used
// 'over'   count > N — genuinely possible (over-redeemed, or a catalog `max`
//          edited down after the sale). Shown rose and never clamped: a silently
//          corrected number would hide a real billing problem.
// 'open'   no usable denominator — render "n ครั้ง", never "n/0".
// 'dead'   the pattern can never be selected by the billing engine — see
//          isPackageDead. Never shown as a balance; it is a catalog fault.
export function packageState(row) {
  // 'dead' outranks everything: the package cannot bill, so n/N is meaningless.
  if (isPackageDead(row)) return 'dead'
  const n = packageUsed(row)
  const N = packageN(row)
  if (!N) return 'open'
  if (n > N) return 'over'
  if (n >= N) return 'done'
  return n > 0 ? 'active' : 'unused'
}

export const isPackageUsable = (row) => {
  const s = packageState(row)
  return s === 'unused' || s === 'active' || s === 'open'
}

// Remove is allowed ONLY on a package nothing has been billed against.
// Removing a USED package is a soft delete (active:false) — but GET /upsale filters
// `promotion_patient.active == true` in BOTH its branches, so the row would vanish
// from the seller's commission report while the `commission` row payroll pays from
// still exists. And a remove-then-reassign mints a NEW promotion_patient id, which
// re-arms api 189's `db.has commission` guard (seller paid twice) and restarts the
// patient at step 1 of a discount they already consumed.
// ⚠️ INCOMPLETE, KNOWINGLY. `count` only sees billing rows with `active == true`,
// so VOIDING a redemption drops it back to 0 and re-opens removal — while the
// `commission` row api 189 minted still exists. A remove-then-reassign then mints a
// new promotion_patient id, re-arming api 189's `db.has commission` guard, and the
// seller is paid twice. Nothing readable from the browser or a Pages Function can
// see an inactive billing row (`GET /billing` has no promotion_patient_id input and
// filters active), so this cannot be closed here. The real fix is a precondition in
// Xano api 424 on `db.has commission {promotion_patient_id}`. Raised with Ram.
export const canRemovePackage = (row) => packageUsed(row) === 0

export const packageProgress = (row) => {
  const N = packageN(row)
  if (!N) return 0
  return Math.min(1, packageUsed(row) / N)
}

/* ── accessors (tolerant — nothing in src/ has ever called this endpoint) ──── */
export const promoName = (row) => row?._promotion?.Name || (row?.promotion_id ? `โปรโมชั่น #${row.promotion_id}` : '—')
export const promoDetail = (row) => row?._promotion?.Detail || ''
export const promoMaintype = (row) => row?._promotion?.Maintype || ''
export const promoSubtype = (row) => row?._promotion?.Subtype || ''
export const packageSeller = (row) => row?._seller?.code || (row?.seller ? `#${row.seller}` : '—')
export const packageVisits = (row) => (Array.isArray(row?.treatment) ? row.treatment : [])

// The schedule, in true charging order, with each step's count taken from the GAP
// in repeat_acc (readPattern already encodes that rule and its reasoning).
// `pattern` is one toggle away from disappearing — the block that would strip it
// sits in a DISABLED !group in api 185 — so an empty result must render as absent,
// not as an empty box.
export const packageSteps = (row) => (patternRows(row).length ? readPattern(row) : [])

// A step's price. NEGATIVE IS ROUTINE — the coupon promotions store -200/-300/-500
// — and must read as a discount, never as "-300".
//
// A step priced 0 IS free, as of 2026-09-01. It used not to be: both write paths fell
// back to the billing type's own price whenever the step read 0, so this said
// "ตามราคาปกติ". That fallback is gone — the promotion dictates the price, ฿0 included
// — so ฿0 must now read as ฟรี, or the counter is quoting a charge the package does
// not make. See knowledge/xano/promotion-price-zero.md.
export function stepPriceLabel(price) {
  const n = Number(price) || 0
  if (n < 0) return `ลด ${baht(Math.abs(n))}`
  if (n === 0) return 'ฟรี'
  return baht(n)
}

// Sort: still usable first, then newest sold. Within that, unused before partly-used
// so the coupons nobody has touched surface at the top.
const ORDER = { unused: 0, active: 1, open: 2, over: 3, dead: 4, done: 5 }
export function comparePackages(a, b) {
  const d = (ORDER[packageState(a)] ?? 9) - (ORDER[packageState(b)] ?? 9)
  if (d) return d
  return (Number(b?.Creation_Date) || 0) - (Number(a?.Creation_Date) || 0)
}

/* ── what the NEXT redemption will bill ────────────────────────────────────── */

/**
 * The step `POST /promotion_patient/add` would charge if it ran right now, or
 * null if the package is exhausted.
 *
 * This MIRRORS api 189 and must keep mirroring it:
 *
 *     ($promotion.pattern|sort:"repeat_acc":number:true)[$$.repeat_acc > ($billing|count)]|first
 *
 * `readPattern` has already sorted by repeat_acc and turned each step's count into
 * the GAP between consecutive repeat_acc values, so re-accumulating those gaps
 * reproduces the stored repeat_acc sequence exactly (221 of 224 promotions — the
 * three that differ are the already-broken rows readPattern documents). Walking
 * until the running total EXCEEDS `used` is therefore the same selection Xano
 * makes, which is the point: the drawer promises a price, and a preview that
 * disagreed with what actually bills would be worse than showing nothing.
 *
 * ⚠️ PREVIEW ONLY — never send these figures anywhere. `price: 0` means "fall back
 * to the billing type's own price", and `fixedDF: null` means "pay the doctor their
 * percentage"; both are resolved inside api 189 against the live catalogue. What
 * bills is whatever Xano computes at the moment of the write.
 */
export function packageNextStep(row) {
  if (!isPackageUsable(row)) return null
  const steps = packageSteps(row)
  if (!steps.length) return null
  const used = packageUsed(row)
  let acc = 0
  for (let i = 0; i < steps.length; i++) {
    acc += steps[i].repeat
    if (acc > used) return { ...steps[i], index: i, ordinal: used + 1 }
  }
  return null
}

/**
 * Can this package be redeemed into a treatment right now?
 *
 * ⚠️ THIS GATE IS LOAD-BEARING — api 189 has NO precondition of its own.
 * When every installment is spent, its `|first` yields null and the stack carries
 * straight on to `db.add billing` with a null `billing_type_id` and a null price,
 * writing a junk line onto the patient's ledger. Nothing downstream rejects it.
 * So the UI must refuse first, and `packageNextStep` returning null IS the refusal.
 * (The durable fix is a precondition in Xano; raised with Ram.)
 */
export const canRedeem = (row) => packageNextStep(row) !== null
